import type { StepType } from '../types';

/**
 * Centralized condition definitions for funnel edge routing.
 *
 * Each condition key matches the orchestrator's `getAvailableConditions()` names.
 * Categories control filtering in both the quick-set pills and the ConditionBuilder.
 */

export type ConditionCategory =
  | 'payment'
  | 'offer'
  | 'mainOrder'
  | 'lastOrder'
  | 'customer'
  | 'generic';

export interface QuickCondition {
  key: string;
  label: string;
  icon: string;
  category: ConditionCategory;
}

const PAYMENT_CONDITIONS: QuickCondition[] = [
  { key: 'payment.success', label: 'Payment Success', icon: '✓', category: 'payment' },
  { key: 'payment.failed', label: 'Payment Failed', icon: '✗', category: 'payment' },
];

const OFFER_CONDITIONS: QuickCondition[] = [
  { key: 'offer.accepted', label: 'Accepted', icon: '✓', category: 'offer' },
  { key: 'offer.declined', label: 'Declined', icon: '✗', category: 'offer' },
];

const ORDER_CONDITIONS: QuickCondition[] = [
  { key: 'mainOrder.exists', label: 'Order Exists', icon: '📦', category: 'mainOrder' },
];

const CUSTOMER_CONDITIONS: QuickCondition[] = [
  { key: 'customer.onMobile', label: 'Mobile', icon: '📱', category: 'customer' },
  { key: 'customer.fromEU', label: 'EU Visitor', icon: '🇪🇺', category: 'customer' },
];

const ALWAYS_CONDITION: QuickCondition = {
  key: 'always', label: 'Always', icon: '→', category: 'generic',
};

/**
 * Maps each source step type to the quick conditions that are relevant
 * when routing *away* from that step.
 *
 * Rules:
 * - checkout  → payment + order conditions  (did they pay?)
 * - offer     → offer conditions            (did they accept?)
 * - landing   → customer conditions         (who are they?)
 * - others    → just "always"
 *
 * "always" is always appended as the last option.
 */
const QUICK_CONDITIONS_BY_SOURCE: Record<string, QuickCondition[]> = {
  checkout: [...PAYMENT_CONDITIONS, ...ORDER_CONDITIONS],
  offer: [...OFFER_CONDITIONS],
  landing: [...CUSTOMER_CONDITIONS],
};

export function getQuickConditionsForSource(sourceType: StepType | string): QuickCondition[] {
  const specific = QUICK_CONDITIONS_BY_SOURCE[sourceType] ?? [];
  return [...specific, ALWAYS_CONDITION];
}

/**
 * Which condition categories are valid after a given source step type.
 * Used by ConditionBuilder for advanced filtering.
 */
const VALID_CATEGORIES_BY_SOURCE: Record<string, ConditionCategory[]> = {
  checkout: ['payment', 'mainOrder', 'customer', 'generic'],
  offer: ['offer', 'lastOrder', 'customer', 'generic'],
  landing: ['customer', 'generic'],
  thankyou: ['customer', 'generic'],
  custom: ['customer', 'generic'],
  external: ['generic'],
  error: ['generic'],
  storefront_shopify: ['customer', 'generic'],
  storefront_woocommerce: ['customer', 'generic'],
};

export function getValidCategoriesForSource(sourceType: StepType | string): ConditionCategory[] {
  return VALID_CATEGORIES_BY_SOURCE[sourceType] ?? ['customer', 'generic'];
}
